import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { BookOpen, Users, Star, Lock, ChevronUp, ChevronDown, Filter, HelpCircle } from 'lucide-react';
import { getPartnerDashboardData } from '@/lib/partner-portal';
import FAQSection from '@/components/FAQSection';

/**
 * Partner Portal Dashboard
 * 
 * Public-facing dashboard that allows clients to view their training program data
 * via secure token-based URLs without requiring authentication.
 */

const PartnerPortal = () => {
  const { token } = useParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programFilter, setProgramFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [faqs, setFaqs] = useState(null);

  // Scroll to FAQ section
  const scrollToFAQ = () => {
    const faqElement = document.getElementById('faq-section');
    if (faqElement) {
      faqElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('No access token provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch dashboard data
        const dashboardData = await getPartnerDashboardData(token);
        
        setDashboardData(dashboardData);
        
        // Set FAQ data from API response
        setFaqs(dashboardData.faqs || {});
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!dashboardData) {
    return <ErrorState error="No data available" />;
  }

  const { client, programs, metrics } = dashboardData;

  // Filter programs based on selected filters (program name, status, type)
  const filteredPrograms = programs.filter(program => {
    // Program name filter
    if (programFilter !== 'all') {
      const name = program.programName;
      let programName;
      if (typeof name === 'string') {
        programName = name.trim();
      } else if (Array.isArray(name)) {
        programName = name[0]?.trim();
      } else if (name && typeof name === 'object' && name.toString) {
        programName = name.toString().trim();
      }
      if (programName !== programFilter) return false;
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      if (!program.status || program.status !== statusFilter) return false;
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      if (!program.type || program.type !== typeFilter) return false;
    }
    
    return true;
  });

  // Get unique program names from client's programs for filter options
  // Handle programName which might be a string, array, or other type from Airtable lookup
  const programNames = programs.map(p => {
    const name = p.programName;
    if (typeof name === 'string') {
      return name.trim();
    } else if (Array.isArray(name)) {
      return name[0]?.trim(); // Take first item if it's an array
    } else if (name && typeof name === 'object' && name.toString) {
      return name.toString().trim(); // Convert object to string
    }
    return null;
  }).filter(Boolean);
  
  const clientProgramNames = [...new Set(programNames)].sort();
  const shouldShowProgramFilter = clientProgramNames.length > 1;
  
  // Get unique statuses and types for filter options
  const clientStatuses = [...new Set(programs.map(p => p.status).filter(Boolean))].sort();
  const clientTypes = [...new Set(programs.map(p => p.type).filter(Boolean))].sort();
  
  const shouldShowStatusFilter = clientStatuses.length > 1;
  const shouldShowTypeFilter = clientTypes.length > 1;
  const shouldShowAnyFilter = shouldShowProgramFilter || shouldShowStatusFilter || shouldShowTypeFilter;
  
  // Check if FAQs are available to show the FAQ button
  const hasFAQs = faqs && Object.keys(faqs).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {client.name.charAt(0)}
                </span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {client.name}
                </h1>
              </div>
            </div>
            
            {/* FAQ Navigation Button */}
            {hasFAQs && (
              <Button
                onClick={scrollToFAQ}
                variant="ghost"
                className="flex items-center gap-2"
                style={{ minHeight: '44px', minWidth: '44px' }} // WCAG minimum touch target
                aria-label="Go to FAQ section"
                aria-controls="faq-section"
              >
                <HelpCircle className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">FAQ</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Total Programs"
            value={metrics.programCount}
            description="Contracted"
            icon={<BookOpen className="h-8 w-8" style={{ color: '#1a3cfa' }} />}
          />
          <MetricCard
            title="Total Participants"
            value={metrics.totalAttendees}
            description="Across all programs"
            icon={<Users className="h-8 w-8" style={{ color: '#1a3cfa' }} />}
          />
          <MetricCard
            title="Average NPS"
            value="Survey info coming soon"
            description="Participant satisfaction"
            icon={<Star className="h-8 w-8" style={{ color: '#1a3cfa' }} />}
          />
        </div>

        {/* Programs Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle>Training Programs</CardTitle>
                <CardDescription>
                  Overview of all training programs for {client.name}
                </CardDescription>
              </div>
              {shouldShowAnyFilter && (
                <div className="flex flex-wrap items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  
                  {shouldShowProgramFilter && (
                    <select
                      value={programFilter}
                      onChange={(e) => setProgramFilter(e.target.value)}
                      className="min-w-[180px] px-3 py-2 border border-gray-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Programs ({clientProgramNames.length})</option>
                      {clientProgramNames.map(programName => (
                        <option key={programName} value={programName}>
                          {programName}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {shouldShowStatusFilter && (
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="min-w-[140px] px-3 py-2 border border-gray-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Statuses</option>
                      {clientStatuses.map(status => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {shouldShowTypeFilter && (
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="min-w-[120px] px-3 py-2 border border-gray-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Types</option>
                      {clientTypes.map(type => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {(programFilter !== 'all' || statusFilter !== 'all' || typeFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setProgramFilter('all');
                        setStatusFilter('all');
                        setTypeFilter('all');
                      }}
                      className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {filteredPrograms.length > 0 ? (
              <ProgramsTable programs={filteredPrograms} />
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {programFilter === 'all' && statusFilter === 'all' && typeFilter === 'all'
                    ? 'No programs found for this client.'
                    : 'No programs match the selected filters.'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div id="faq-section" className="mt-8">
          <FAQSection faqs={faqs} />
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="text-sm text-gray-500 mb-4 sm:mb-0">
              Last updated: {new Date(dashboardData.lastUpdated).toLocaleString()}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <span>❤️ Powered by </span>
              <a 
                href="https://avenuecareers.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center hover:text-gray-700 transition-colors"
              >
                <img 
                  src="/avenue-logo-small.png" 
                  alt="Avenue" 
                  className="h-5 mr-1"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'inline';
                  }}
                />
                <span style={{ display: 'none' }}>Avenue</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ title, value, description, icon }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
        <div className="opacity-80">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

/**
 * Programs Table Component
 */
const ProgramsTable = ({ programs }) => {
  const [sortField, setSortField] = useState('deliveryStart');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPrograms = [...programs].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle date fields
    if (sortField === 'deliveryStart' || sortField === 'deliveryEnd') {
      aValue = aValue ? new Date(aValue) : new Date('1970-01-01');
      bValue = bValue ? new Date(bValue) : new Date('1970-01-01');
    }

    // Handle string fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle numeric fields
    if (sortField === 'sessionCount' || sortField === 'attendeeCount') {
      aValue = aValue || 0;
      bValue = bValue || 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortableHeader = ({ field, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )
        ) : (
          <div className="h-4 w-4" />
        )}
      </div>
    </TableHead>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    if (!status) return 'secondary';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('complete') || statusLower.includes('delivered')) {
      return 'success';
    }
    if (statusLower.includes('progress') || statusLower.includes('active')) {
      return 'warning';
    }
    if (statusLower.includes('cancel')) {
      return 'destructive';
    }
    return 'secondary';
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader field="programName">Cohort</SortableHeader>
          <SortableHeader field="type">Type</SortableHeader>
          <SortableHeader field="status">Status</SortableHeader>
          <SortableHeader field="deliveryStart">Start Date</SortableHeader>
          <SortableHeader field="deliveryEnd">End Date</SortableHeader>
          <TableHead className="text-right cursor-pointer hover:bg-gray-50 select-none" onClick={() => handleSort('sessionCount')}>
            <div className="flex items-center justify-end gap-1">
              Sessions
              {sortField === 'sessionCount' ? (
                sortDirection === 'asc' ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )
              ) : (
                <div className="h-4 w-4" />
              )}
            </div>
          </TableHead>
          <TableHead className="text-right cursor-pointer hover:bg-gray-50 select-none" onClick={() => handleSort('attendeeCount')}>
            <div className="flex items-center justify-end gap-1">
              Participants
              {sortField === 'attendeeCount' ? (
                sortDirection === 'asc' ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )
              ) : (
                <div className="h-4 w-4" />
              )}
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPrograms.map((program) => (
          <TableRow key={program.id}>
            <TableCell className="font-medium">
              <div>
                <div className="font-semibold">{program.programName}</div>
                <div className="text-sm text-gray-500">
                  SO #{program.soNumber}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex flex-col">
                <span className="text-sm">{program.type}</span>
                {program.virtualPlatform && (
                  <span className="text-xs text-gray-500">
                    {program.virtualPlatform}
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={getStatusColor(program.status)}>
                {program.status || 'Unknown'}
              </Badge>
            </TableCell>
            <TableCell>{formatDate(program.deliveryStart)}</TableCell>
            <TableCell>{formatDate(program.deliveryEnd)}</TableCell>
            <TableCell className="text-right font-semibold">
              {program.sessionCount}
            </TableCell>
            <TableCell className="text-right font-semibold">
              {program.attendeeCount}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Loading State Component
 */
const LoadingState = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <Spinner className="h-8 w-8 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Loading Dashboard
      </h2>
      <p className="text-gray-500">
        Please wait while we fetch your program data...
      </p>
    </div>
  </div>
);

/**
 * Error State Component
 */
const ErrorState = ({ error }) => {
  const getErrorMessage = (error) => {
    if (error === 'Invalid or disabled token') {
      return {
        title: 'Access Denied',
        message: 'This dashboard link is invalid or has been disabled. Please contact Avenue Careers for a new access link.',
        action: 'Contact Support'
      };
    }
    
    if (error === 'Invalid token format') {
      return {
        title: 'Invalid Link',
        message: 'The dashboard link appears to be malformed. Please check the URL and try again.',
        action: 'Check URL'
      };
    }
    
    return {
      title: 'Something went wrong',
      message: 'We encountered an error while loading your dashboard. Please try again or contact support if the problem persists.',
      action: 'Try Again'
    };
  };

  const { title, message } = getErrorMessage(error);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center">
        <div className="mb-6">
          <Lock className="h-16 w-16 mx-auto" style={{ color: '#1a3cfa' }} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-gray-600 mb-8">{message}</p>
        
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">Need help?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Contact Avenue for assistance accessing your dashboard.
            </p>
            <a 
              href="mailto:support@avenuecareers.com" 
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </a>
          </CardContent>
        </Card>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-center text-sm text-gray-500">
            <span>❤️ Powered by </span>
            <a 
              href="https://avenuecareers.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center hover:text-gray-700 transition-colors"
            >
              <img 
                src="/avenue-logo-small.png" 
                alt="Avenue" 
                className="h-5 mr-1"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span style={{ display: 'none' }}>Avenue</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPortal;